import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { BaseComponent } from '../../../base/base.component';
import { CheckboxComponent } from '../../../core/controls/checkbox.component';
import { FilterSection, ICriteria } from '../edit-filter.component';

@Component({
  selector: 'app-claim-flag-criteria',
  imports: [CommonModule, CheckboxComponent],
  template: `
    <div class="tw-pt-0">
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-2 tw-w-full">
        <ng-template #checkbox let-control="control" let-label="label">
          <app-checkbox
            [control]="control"
            [label]="label"
            [showHelpText]="false"
          />
        </ng-template>

        @for (claimFlag of claimFlags; track claimFlag) {
          <ng-container
            *ngTemplateOutlet="
              checkbox;
              context: {
                label: claimFlag.label,
                control: claimFlag.control,
              }
            "
          ></ng-container>
        }
      </div>
    </div>
  `,
  styles: ``,
})
export class ClaimFlagCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  @Input({ required: true })
  section: FilterSection;

  claimFlags: ClaimFlag[] = [
    {
      label: 'Apportion',
      system: 'juris',
      table: 'claim',
      field: 'APPORTIONMENT_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Death',
      system: 'juris',
      table: 'claim',
      field: 'DEATH_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'ERISA',
      system: 'juris',
      table: 'claim',
      field: 'ERISA_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'ERISA appeal decision due',
      system: 'juris',
      table: 'erisa_decision_due_filter',
      field: 'DATE_DECISION_DUE_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Excess',
      system: 'juris',
      table: 'claim',
      field: 'EXCESS_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'ICD at-risk',
      system: 'juris',
      table: 'claim_icd_as_of',
      field: 'AT_RISK_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'ICD midrange',
      system: 'juris',
      table: 'claim_icd_as_of',
      field: 'MIDRANGE_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Litigation',
      system: 'juris',
      table: 'claimant',
      field: 'CLMNT_LITIGATE',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Managed care',
      system: 'juris',
      table: 'claim_mc_refer',
      field: 'DATE_OPEN_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Medicare beneficiary',
      system: 'juris',
      table: 'claimant',
      field: 'MEDICARE_BENEFICIARY_STATUS',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Nurse triage',
      system: 'juris',
      table: 'claim_note_type_nt',
      field: 'NOTE_TYPE_NT_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Overpayment balance',
      system: 'juris',
      table: 'clm_master_grp_overpay',
      field: 'OVERPAYMENT_BALANCE',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Overpayment recovered $0',
      system: 'juris',
      table: 'claim_master_group_flag',
      field: 'OVERPAYMENT_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Rehabilitation',
      system: 'juris',
      table: 'claim',
      field: 'REHAB_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Relapse',
      system: 'juris',
      table: 'claim_loss_info',
      field: 'RELAPSE_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'SIF accepted by fund',
      system: 'juris',
      table: 'claim_loss_info',
      field: 'DATE_ACCEPTED_BY_FUND_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'SIF notice filed',
      system: 'juris',
      table: 'claim_loss_info',
      field: 'DATE_SIF_FILED_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Subrogation',
      system: 'juris',
      table: 'claim',
      field: 'SUBROGATION_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Tender',
      system: 'juris',
      table: 'claim',
      field: 'TENDERED_FLAG',
      control: new FormControl<boolean>(false),
    },
    {
      label: 'Transitional work program',
      system: 'juris',
      table: 'work_status_as_of',
      field: 'WELL_AT_WORK_FLAG',
      control: new FormControl<boolean>(false),
    },
  ];

  setFlagsFromCriteria(criteria: FilterCriteria[]) {
    this.claimFlags.forEach((flag) => {
      const found = criteria.some(
        (c) =>
          c.tableAlias === flag.table &&
          c.column === flag.field &&
          c.operator === 'EQUALS' &&
          c.values?.[0] === 'Y',
      );
      flag.control.setValue(found);
    });
  }

  async ngOnInit() {
    this.section.criteria = this;

    if (this.section.values?.length) {
      this.setFlagsFromCriteria(this.section.values);
    }

    this.claimFlags.forEach((flag) => {
      this.dispose.subs = flag.control.valueChanges.subscribe(() => {
        this.emitCount();
      });
    });
  }

  private emitCount() {
    this.section.count.set(
      this.claimFlags.filter((flag) => flag.control.value).length,
    );
  }

  valid(): boolean {
    return true;
  }

  getCriteria(): FilterCriteria[] {
    return this.claimFlags
      .filter((flag) => flag.control.value)
      .map(
        (flag) =>
          ({
            tableAlias: flag.table,
            column: flag.field,
            displayName: flag.label,
            operator: 'EQUALS',
            values: ['Y'],
            lineOfBusiness: '',
            dataType: '',
            isClaimLab: false,
            formattedOperator: '',
            formattedValue: '',
            filterSection: 'Flag',
            filterSubsection: '',
          }) as FilterCriteria,
      );
  }

  clearCriteria(): void {
    this.claimFlags.forEach((flag) => {
      flag.control.setValue(false, { emitEvent: false });
    });
    this.section.count.set(0);
  }
}

interface ClaimFlag {
  label: string;
  system: string;
  table: string;
  field: string;
  control: FormControl<boolean>;
}
